
import { useState } from 'react';

export default function HeroSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('email', email);
      
      const response = await fetch('https://readdy.ai/api/form/d4ciqpudhknumrhf670g', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/5c5f1477086e0f7a8389e060f22e8d3e.jpg')`
          }}
        ></div>
        
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          {/* Coming Soon Badge */}
          <div className="inline-flex items-center bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-full px-6 py-2 mb-8">
            <i className="ri-time-line w-4 h-4 flex items-center justify-center text-blue-600 mr-2"></i>
            <span className="text-blue-800 font-semibold text-sm">Coming Soon - Revolutionary Prop Firm Monitoring</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-black leading-tight mb-8">
            You Trade,<br />
            <span className="text-black">
              Let Us Police
            </span><br />
            Your PropFirm Payouts
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-12">
            We have developed a proprietary system that will alert you when there are delayed payouts, 
            KYC issues, negative Trustpilot reviews, and bad business practices.
          </p>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-alarm-warning-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Delayed Payouts</h3>
              <p className="text-gray-600 text-sm">Instant alerts when your prop firm delays payouts beyond normal timeframes</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-shield-user-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">KYC Issues</h3>
              <p className="text-gray-600 text-sm">Monitor verification problems and compliance issues affecting traders</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-star-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Trustpilot Reviews</h3>
              <p className="text-gray-600 text-sm">Real-time monitoring of negative reviews and reputation changes</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-shield-check-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Business Practices</h3>
              <p className="text-gray-600 text-sm">Track rule changes, policy updates, and questionable business practices</p>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div id="signup" className="bg-white border border-gray-200 rounded-3xl p-8 shadow-xl mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Sign Up Now for Our Mailing List</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Get the latest news of our launch, special firm promotions, and exclusive account giveaways. 
              Be the first to know when we go live!
            </p>
            
            {isSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line w-6 h-6 flex items-center justify-center text-white"></i>
                  </div>
                </div>
                <h3 className="text-green-800 font-bold text-lg mb-2">Thank You!</h3>
                <p className="text-green-700">You've been successfully added to our mailing list. We'll keep you updated on our launch and exclusive offers.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="max-w-md mx-auto" data-readdy-form id="newsletter_signup">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-6 py-4 border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold hover:shadow-lg transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
                  >
                    {isSubmitting ? 'Signing Up...' : 'Get Early Access'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* How It Works */}
          <div id="how-it-works" className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-3xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Sign Up</h3>
                <p className="text-gray-600">Simply sign up for customized alerts of your specified prop firm</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Monitor</h3>
                <p className="text-gray-600">Our proprietary system continuously monitors your selected firms</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Get Alerts</h3>
                <p className="text-gray-600">Receive instant notifications about issues and latest promo codes</p>
              </div>
            </div>
          </div>

          {/* Bonus Features */}
          <div id="features" className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-white mb-16">
            <h2 className="text-3xl font-bold mb-6">Bonus Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-coupon-line w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold mb-1">Latest Promo Codes</h3>
                  <p className="text-blue-100">Get exclusive discount codes as soon as they go live</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-gift-line w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold mb-1">Account Giveaways</h3>
                  <p className="text-blue-100">Exclusive access to funded account giveaways and promotions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">24/7</div>
              <p className="text-gray-600">Continuous Monitoring</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
              <p className="text-gray-600">Prop Firms Tracked</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">Real-time</div>
              <p className="text-gray-600">Instant Alerts</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
